import { useState, useEffect } from 'react';
import { Search, Plus, Edit, UserCheck, UserX, Filter, Loader2, X, Save, Mail, CheckCircle, Wallet, History, ChevronDown, ChevronUp, CalendarPlus, Check, AlertCircle, FileText } from 'lucide-react';
import { userApi, balanceApi, adminApi } from '../../services/api';

interface User {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  employeeNumber?: string;
  department?: string;
  departmentName?: string;
  site?: string;
  jobTitle?: string;
  role: string;
  isActive: boolean;
  supervisorId?: string;
  supervisorName?: string;
  reportsDirectlyToDirector?: boolean;
  cognitoUserId?: string;
  invitedAt?: string;
  startDate?: string;
}

interface LeaveBalance {
  leaveType: string;
  year: number;
  total: number;
  taken: number;
  pending: number;
  available: number;
  carryOver: number;
}

interface BalanceHistoryItem {
  auditId: string;
  auditType: string;
  leaveType: string;
  year: number;
  previousBalance: {
    total: number;
    taken: number;
    available: number;
    carryOver: number;
  } | null;
  newBalance: {
    total: number;
    taken: number;
    available: number;
    carryOver: number;
  };
  daysDeducted?: number;
  daysRestored?: number;
  applicationId?: string;
  reason: string;
  performedByName: string;
  timestamp: string;
}

const ROLES = [
  { value: 'EMPLOYEE', label: 'Employee' },
  { value: 'SUPERVISOR', label: 'Supervisor' },
  { value: 'DIRECTOR', label: 'Director' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'BOARD_APPROVER', label: 'Board Approver' },
];

const DEPARTMENTS = [
  { id: 'social-work', name: 'Social Work' },
  { id: 'admin-support', name: 'Administration and Support' },
  { id: 'rotary-haven', name: 'Rotary Haven ECD' },
  { id: 'sonstraaltjies', name: 'Sonstraaltjies ECD' },
  { id: 'vrolike-vinkies', name: 'Vrolike Vinkies ECD' },
  { id: 'mary-makwela', name: 'Mary Makwela House' },
  { id: 'lighthouse', name: 'Lighthouse House' },
  { id: 'nehemia', name: 'Nehemia House' },
  { id: 'hcfs-shop', name: 'HCFS Shop' },
  { id: 'board', name: 'Board' },
];

// Leave types that have allocations
const LEAVE_TYPES_WITH_ALLOCATION = [
  { code: 'ANNUAL', name: 'Annual Leave', defaultTotal: 24, description: '24 days/year' },
  { code: 'SICK', name: 'Sick Leave', defaultTotal: 36, description: '36 days/3-year cycle' },
  { code: 'FAMILY', name: 'Family Responsibility', defaultTotal: 3, description: '3 days/year' },
];

export default function UserManagementPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  // Invite state
  const [invitingUserId, setInvitingUserId] = useState<string | null>(null);
  const [inviteMessage, setInviteMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Add modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  // Balance modal state
  const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false);
  const [balanceUser, setBalanceUser] = useState<User | null>(null);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);
  const [isSavingBalances, setIsSavingBalances] = useState(false);
  const [balanceError, setBalanceError] = useState('');
  const [balanceSuccess, setBalanceSuccess] = useState('');
  const [, setBalances] = useState<LeaveBalance[]>([]);
  const [balanceYear, setBalanceYear] = useState(new Date().getFullYear());
  const [balanceForm, setBalanceForm] = useState<Record<string, { total: number; taken: number; carryOver: number }>>({});
  const [balanceReason, setBalanceReason] = useState('');
  const [balanceHistory, setBalanceHistory] = useState<BalanceHistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Bulk allocation modal state
  const [isBulkAllocationOpen, setIsBulkAllocationOpen] = useState(false);
  const [bulkAllocationYear, setBulkAllocationYear] = useState(new Date().getFullYear());
  const [bulkAllocationProgress, setBulkAllocationProgress] = useState<{
    status: 'idle' | 'processing' | 'complete';
    current: number;
    total: number;
    results: Array<{ userId: string; name: string; success: boolean; error?: string }>;
  }>({ status: 'idle', current: 0, total: 0, results: [] });
  const [bulkExcludedUsers, setBulkExcludedUsers] = useState<Set<string>>(new Set());
  const [bulkAllocations, setBulkAllocations] = useState({ annual: 24, sick: 36, family: 3 });

  // Manual leave modal state
  const [isManualLeaveOpen, setIsManualLeaveOpen] = useState(false);
  const [manualLeaveUser, setManualLeaveUser] = useState<User | null>(null);
  const [isCreatingLeave, setIsCreatingLeave] = useState(false);
  const [manualLeaveError, setManualLeaveError] = useState('');
  const [manualLeaveSuccess, setManualLeaveSuccess] = useState('');
  const [manualLeaveForm, setManualLeaveForm] = useState({
    leaveType: 'ANNUAL',
    startDate: '',
    endDate: '',
    totalDays: 1,
    reason: '',
    notes: '',
  });

  // Add form state
  const [addForm, setAddForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    jobTitle: '',
    department: '',
    site: '',
    role: 'EMPLOYEE',
    supervisorId: '',
    reportsDirectlyToDirector: false,
    startDate: new Date().toISOString().split('T')[0],
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    jobTitle: '',
    department: '',
    site: '',
    role: '',
    supervisorId: '',
    reportsDirectlyToDirector: false,
    isActive: true,
    startDate: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await userApi.listUsers();
      const fetchedUsers = (response as { users: User[] }).users || [];
      setUsers(fetchedUsers);

      // Extract unique departments from users (prefer departmentName)
      const uniqueDepts = [...new Set(fetchedUsers.map(u => u.departmentName || u.department).filter((d): d is string => Boolean(d)))];
      setDepartments(uniqueDepts.sort());
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      jobTitle: user.jobTitle || '',
      department: user.department || '',
      site: user.site || '',
      role: user.role || 'EMPLOYEE',
      supervisorId: user.supervisorId || '',
      reportsDirectlyToDirector: user.reportsDirectlyToDirector || false,
      isActive: user.isActive !== false,
      startDate: user.startDate || '',
    });
    setSaveError('');
    setSaveSuccess('');
    setIsEditModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
    setSaveError('');
    setSaveSuccess('');
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    setIsSaving(true);
    setSaveError('');
    setSaveSuccess('');

    try {
      // Find department name from department ID
      const deptInfo = DEPARTMENTS.find(d => d.id === editForm.department);

      // Find supervisor name
      const supervisor = users.find(u => u.userId === editForm.supervisorId);
      const supervisorName = supervisor ? `${supervisor.firstName} ${supervisor.lastName}` : undefined;

      await userApi.updateUser(selectedUser.userId, {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        jobTitle: editForm.jobTitle,
        department: editForm.department,
        departmentName: deptInfo?.name,
        site: editForm.site,
        role: editForm.role,
        supervisorId: editForm.reportsDirectlyToDirector ? undefined : editForm.supervisorId || undefined,
        supervisorName: editForm.reportsDirectlyToDirector ? undefined : supervisorName,
        reportsDirectlyToDirector: editForm.reportsDirectlyToDirector,
        isActive: editForm.isActive,
        startDate: editForm.startDate || undefined,
      });

      setSaveSuccess('User updated successfully');

      // Refresh users list
      await fetchUsers();

      // Close modal after short delay
      setTimeout(() => {
        handleCloseModal();
      }, 1000);
    } catch (err: any) {
      console.error('Error updating user:', err);
      setSaveError(err.message || 'Failed to update user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInviteUser = async (user: User) => {
    setInvitingUserId(user.userId);
    setInviteMessage(null);

    try {
      const result = await userApi.inviteUser(user.userId);
      setInviteMessage({
        type: 'success',
        text: `Invitation sent to ${result.email}`,
      });
      // Refresh users to update the invite status
      await fetchUsers();
    } catch (err: any) {
      console.error('Error inviting user:', err);
      setInviteMessage({
        type: 'error',
        text: err.message || 'Failed to send invitation',
      });
    } finally {
      setInvitingUserId(null);
      // Clear message after 5 seconds
      setTimeout(() => setInviteMessage(null), 5000);
    }
  };

  const handleOpenAddModal = () => {
    setAddForm({
      email: '',
      firstName: '',
      lastName: '',
      jobTitle: '',
      department: '',
      site: '',
      role: 'EMPLOYEE',
      supervisorId: '',
      reportsDirectlyToDirector: false,
      startDate: new Date().toISOString().split('T')[0],
    });
    setCreateError('');
    setCreateSuccess('');
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setCreateError('');
    setCreateSuccess('');
  };

  const handleCreateUser = async () => {
    setIsCreating(true);
    setCreateError('');
    setCreateSuccess('');

    // Validate
    if (!addForm.email || !addForm.firstName || !addForm.lastName) {
      setCreateError('Email, First Name, and Last Name are required');
      setIsCreating(false);
      return;
    }

    try {
      // Find department name from department ID
      const deptInfo = DEPARTMENTS.find(d => d.id === addForm.department);

      // Find supervisor name
      const supervisor = users.find(u => u.userId === addForm.supervisorId);
      const supervisorName = supervisor ? `${supervisor.firstName} ${supervisor.lastName}` : undefined;

      await userApi.createUser({
        email: addForm.email,
        firstName: addForm.firstName,
        lastName: addForm.lastName,
        jobTitle: addForm.jobTitle,
        department: addForm.department,
        departmentName: deptInfo?.name,
        site: addForm.site,
        role: addForm.role,
        supervisorId: addForm.reportsDirectlyToDirector ? undefined : addForm.supervisorId || undefined,
        supervisorName: addForm.reportsDirectlyToDirector ? undefined : supervisorName,
        reportsDirectlyToDirector: addForm.reportsDirectlyToDirector,
      });

      setCreateSuccess('User created successfully');

      // Refresh users list
      await fetchUsers();

      // Close modal after short delay
      setTimeout(() => {
        handleCloseAddModal();
      }, 1000);
    } catch (err: any) {
      console.error('Error creating user:', err);
      setCreateError(err.message || 'Failed to create user');
    } finally {
      setIsCreating(false);
    }
  };

  // Balance modal handlers
  const handleOpenBalanceModal = async (user: User) => {
    setBalanceUser(user);
    setBalanceYear(new Date().getFullYear());
    setBalanceError('');
    setBalanceSuccess('');
    setBalanceReason('');
    setBalanceHistory([]);
    setShowHistory(false);
    setIsBalanceModalOpen(true);
    await fetchBalances(user.userId, new Date().getFullYear());
  };

  const handleCloseBalanceModal = () => {
    setIsBalanceModalOpen(false);
    setBalanceUser(null);
    setBalances([]);
    setBalanceForm({});
    setBalanceReason('');
    setBalanceHistory([]);
    setShowHistory(false);
  };

  const fetchBalances = async (userId: string, year: number) => {
    setIsLoadingBalances(true);
    setBalanceError('');

    try {
      const response = await balanceApi.getBalance(userId, { year: String(year) }) as { balances: LeaveBalance[] };
      const fetchedBalances = response.balances || [];
      setBalances(fetchedBalances);

      // Initialize form with fetched or default values
      const formData: Record<string, { total: number; taken: number; carryOver: number }> = {};
      LEAVE_TYPES_WITH_ALLOCATION.forEach(lt => {
        const existing = fetchedBalances.find(b => b.leaveType === lt.code);
        formData[lt.code] = {
          total: existing?.total ?? lt.defaultTotal,
          taken: existing?.taken ?? 0,
          carryOver: existing?.carryOver ?? 0,
        };
      });
      setBalanceForm(formData);
    } catch (err: any) {
      console.error('Error fetching balances:', err);
      // Initialize with defaults if no balances exist
      const formData: Record<string, { total: number; taken: number; carryOver: number }> = {};
      LEAVE_TYPES_WITH_ALLOCATION.forEach(lt => {
        formData[lt.code] = {
          total: lt.defaultTotal,
          taken: 0,
          carryOver: 0,
        };
      });
      setBalanceForm(formData);
    } finally {
      setIsLoadingBalances(false);
    }
  };

  const handleYearChange = async (year: number) => {
    setBalanceYear(year);
    if (balanceUser) {
      await fetchBalances(balanceUser.userId, year);
    }
  };

  const fetchHistory = async (userId: string) => {
    setIsLoadingHistory(true);
    try {
      const response = await balanceApi.getHistory(userId) as { history: BalanceHistoryItem[] };
      setBalanceHistory(response.history || []);
    } catch (err) {
      console.error('Error fetching balance history:', err);
      setBalanceHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleToggleHistory = async () => {
    if (!showHistory && balanceUser && balanceHistory.length === 0) {
      await fetchHistory(balanceUser.userId);
    }
    setShowHistory(!showHistory);
  };

  const formatHistoryTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getLeaveTypeName = (code: string) => {
    const lt = LEAVE_TYPES_WITH_ALLOCATION.find(l => l.code === code);
    return lt?.name || code;
  };

  // Bulk allocation handlers
  const handleOpenBulkAllocation = () => {
    setBulkAllocationYear(new Date().getFullYear());
    setBulkAllocationProgress({ status: 'idle', current: 0, total: 0, results: [] });
    setBulkExcludedUsers(new Set());
    setBulkAllocations({ annual: 24, sick: 36, family: 3 });
    setIsBulkAllocationOpen(true);
  };

  const handleCloseBulkAllocation = () => {
    if (bulkAllocationProgress.status === 'processing') return; // Don't close while processing
    setIsBulkAllocationOpen(false);
    setBulkAllocationProgress({ status: 'idle', current: 0, total: 0, results: [] });
  };

  const getActiveUsers = () => {
    return users.filter(u => u.isActive && u.role !== 'BOARD_APPROVER' && !bulkExcludedUsers.has(u.userId));
  };

  const handleRemoveFromBulkAllocation = (userId: string) => {
    setBulkExcludedUsers(prev => new Set([...prev, userId]));
  };

  const handleStartBulkAllocation = async () => {
    const activeUsers = getActiveUsers();
    if (activeUsers.length === 0) return;

    setBulkAllocationProgress({
      status: 'processing',
      current: 0,
      total: activeUsers.length,
      results: [],
    });

    const results: Array<{ userId: string; name: string; success: boolean; error?: string }> = [];
    const reason = `Annual allocation for ${bulkAllocationYear}`;

    for (let i = 0; i < activeUsers.length; i++) {
      const user = activeUsers[i];
      try {
        // Set each leave type balance with custom amounts
        await balanceApi.setBalance({
          userId: user.userId,
          leaveType: 'ANNUAL',
          year: bulkAllocationYear,
          total: bulkAllocations.annual,
          taken: 0,
          carryOver: 0,
          reason,
        });
        await balanceApi.setBalance({
          userId: user.userId,
          leaveType: 'SICK',
          year: bulkAllocationYear,
          total: bulkAllocations.sick,
          taken: 0,
          carryOver: 0,
          reason,
        });
        await balanceApi.setBalance({
          userId: user.userId,
          leaveType: 'FAMILY',
          year: bulkAllocationYear,
          total: bulkAllocations.family,
          taken: 0,
          carryOver: 0,
          reason,
        });
        results.push({
          userId: user.userId,
          name: `${user.firstName} ${user.lastName}`,
          success: true,
        });
      } catch (err: any) {
        results.push({
          userId: user.userId,
          name: `${user.firstName} ${user.lastName}`,
          success: false,
          error: err.message || 'Failed to allocate',
        });
      }

      setBulkAllocationProgress({
        status: 'processing',
        current: i + 1,
        total: activeUsers.length,
        results: [...results],
      });
    }

    setBulkAllocationProgress({
      status: 'complete',
      current: activeUsers.length,
      total: activeUsers.length,
      results,
    });
  };

  // Manual leave handlers
  const handleOpenManualLeaveModal = (user: User) => {
    setManualLeaveUser(user);
    setManualLeaveForm({
      leaveType: 'ANNUAL',
      startDate: '',
      endDate: '',
      totalDays: 1,
      reason: '',
      notes: '',
    });
    setManualLeaveError('');
    setManualLeaveSuccess('');
    setIsManualLeaveOpen(true);
  };

  const handleCloseManualLeaveModal = () => {
    setIsManualLeaveOpen(false);
    setManualLeaveUser(null);
    setManualLeaveForm({
      leaveType: 'ANNUAL',
      startDate: '',
      endDate: '',
      totalDays: 1,
      reason: '',
      notes: '',
    });
    setManualLeaveError('');
    setManualLeaveSuccess('');
  };

  const handleManualLeaveFormChange = (field: string, value: string | number) => {
    setManualLeaveForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const calculateDaysBetween = (start: string, end: string): number => {
    if (!start || !end) return 1;
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const handleCreateManualLeave = async () => {
    if (!manualLeaveUser) return;

    const { leaveType, startDate, endDate, totalDays, reason } = manualLeaveForm;

    if (!startDate || !endDate || !reason.trim()) {
      setManualLeaveError('Start date, end date, and reason are required');
      return;
    }

    if (new Date(endDate) < new Date(startDate)) {
      setManualLeaveError('End date must be after start date');
      return;
    }

    setIsCreatingLeave(true);
    setManualLeaveError('');
    setManualLeaveSuccess('');

    try {
      await adminApi.createManualLeave({
        userId: manualLeaveUser.userId,
        leaveType,
        startDate,
        endDate,
        totalDays,
        reason: reason.trim(),
        notes: manualLeaveForm.notes.trim() || undefined,
      });

      setManualLeaveSuccess(`Leave record created successfully for ${manualLeaveUser.firstName} ${manualLeaveUser.lastName}`);

      // Clear form after short delay to show success message
      setTimeout(() => {
        handleCloseManualLeaveModal();
      }, 1500);
    } catch (err: any) {
      console.error('Error creating manual leave:', err);
      setManualLeaveError(err.message || 'Failed to create leave record');
    } finally {
      setIsCreatingLeave(false);
    }
  };

  const handleSaveBalances = async () => {
    if (!balanceUser) return;

    if (!balanceReason.trim()) {
      setBalanceError('Please provide a reason for the balance change');
      return;
    }

    setIsSavingBalances(true);
    setBalanceError('');
    setBalanceSuccess('');

    try {
      // Save each leave type balance
      for (const lt of LEAVE_TYPES_WITH_ALLOCATION) {
        const formValues = balanceForm[lt.code];
        if (formValues) {
          await balanceApi.setBalance({
            userId: balanceUser.userId,
            leaveType: lt.code,
            year: balanceYear,
            total: formValues.total,
            taken: formValues.taken,
            carryOver: formValues.carryOver,
            reason: balanceReason.trim(),
          });
        }
      }

      setBalanceSuccess('Balances saved successfully');
      setBalanceReason(''); // Clear reason after successful save

      // Refresh balances
      await fetchBalances(balanceUser.userId, balanceYear);
    } catch (err: any) {
      console.error('Error saving balances:', err);
      setBalanceError(err.message || 'Failed to save balances');
    } finally {
      setIsSavingBalances(false);
    }
  };

  // Get potential supervisors for add modal
  const getPotentialSupervisorsForAdd = () => {
    return users.filter(u =>
      ['SUPERVISOR', 'DIRECTOR'].includes(u.role) &&
      u.isActive
    );
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'DIRECTOR':
        return 'bg-purple-100 text-purple-800';
      case 'ADMIN':
        return 'bg-red-100 text-red-800';
      case 'SUPERVISOR':
        return 'bg-blue-100 text-blue-800';
      case 'BOARD_APPROVER':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const isUserActive = (user: User) => {
    return user.isActive === true;
  };

  // Get potential supervisors (supervisors and director, excluding the selected user)
  const getPotentialSupervisors = () => {
    return users.filter(u =>
      u.userId !== selectedUser?.userId &&
      ['SUPERVISOR', 'DIRECTOR'].includes(u.role) &&
      u.isActive
    );
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch =
      searchTerm === '' ||
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.employeeNumber?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesDepartment =
      departmentFilter === 'all' || (user.departmentName || user.department) === departmentFilter;

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && isUserActive(user)) ||
      (statusFilter === 'inactive' && !isUserActive(user));

    return matchesSearch && matchesDepartment && matchesStatus;
  });

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth(); // 0 = January
  const isJanuary = currentMonth === 0;
  const yearOptions = [currentYear - 1, currentYear, currentYear + 1];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600">Loading users...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-sm text-gray-600">Manage employee accounts, roles, and leave balances</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative group" title={isJanuary ? '' : 'Only available in January'}>
            <button
              onClick={handleOpenBulkAllocation}
              disabled={!isJanuary}
              className={`flex items-center gap-2 ${isJanuary ? 'btn-secondary' : 'btn-secondary opacity-50 cursor-not-allowed'}`}
            >
              <CalendarPlus className="h-4 w-4" />
              Allocate Annual Balances
            </button>
            {!isJanuary && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                Only available in January
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
              </div>
            )}
          </div>
          <button onClick={handleOpenAddModal} className="btn-primary flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add Employee
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Invite Message */}
      {inviteMessage && (
        <div className={`rounded-md p-4 ${inviteMessage.type === 'success' ? 'bg-green-50' : 'bg-red-50'}`}>
          <p className={`text-sm ${inviteMessage.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
            {inviteMessage.text}
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-4 p-4">
          <div className="flex flex-1 items-center gap-2">
            <Search className="h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search by name, email, or employee number..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="input flex-1"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={departmentFilter}
              onChange={e => setDepartmentFilter(e.target.value)}
              className="input w-40"
            >
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="input w-32"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="card overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Employee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Department / Site
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Start Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Status
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                Login
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  No users found matching your criteria
                </td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.userId} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="text-sm text-gray-900">{user.departmentName || user.department || '-'}</div>
                    <div className="text-sm text-gray-500">{user.jobTitle || '-'}</div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${getRoleBadgeColor(
                        user.role
                      )}`}
                    >
                      {user.role?.replace('_', ' ') || 'EMPLOYEE'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {user.startDate ? new Date(user.startDate).toLocaleDateString('en-ZA') : '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    {isUserActive(user) ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                        <UserCheck className="h-3 w-3" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800">
                        <UserX className="h-3 w-3" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-center">
                    {user.cognitoUserId ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800" title="User can log in">
                        <CheckCircle className="h-3 w-3" />
                        Linked
                      </span>
                    ) : (
                      <button
                        onClick={() => handleInviteUser(user)}
                        disabled={invitingUserId === user.userId}
                        className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-800 hover:bg-yellow-200 disabled:opacity-50"
                        title="Send login invitation"
                      >
                        {invitingUserId === user.userId ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Mail className="h-3 w-3" />
                        )}
                        {invitingUserId === user.userId ? 'Sending...' : 'Invite'}
                      </button>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleOpenBalanceModal(user)}
                        className="text-green-600 hover:text-green-900"
                        title="Manage leave balances"
                      >
                        <Wallet className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleOpenManualLeaveModal(user)}
                        className="text-orange-600 hover:text-orange-900"
                        title="Add leave record"
                      >
                        <FileText className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEditClick(user)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Edit user"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {filteredUsers.length} of {users.length} employees
        </p>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={handleCloseModal}
            />

            {/* Modal */}
            <div className="relative w-full max-w-lg rounded-lg bg-white shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Edit Employee
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {saveError && (
                  <div className="rounded-md bg-red-50 p-3">
                    <p className="text-sm text-red-700">{saveError}</p>
                  </div>
                )}
                {saveSuccess && (
                  <div className="rounded-md bg-green-50 p-3">
                    <p className="text-sm text-green-700">{saveSuccess}</p>
                  </div>
                )}

                {/* Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={editForm.firstName}
                      onChange={e => setEditForm({ ...editForm, firstName: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={editForm.lastName}
                      onChange={e => setEditForm({ ...editForm, lastName: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                </div>

                {/* Email (read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={selectedUser.email}
                    disabled
                    className="input w-full bg-gray-100 cursor-not-allowed"
                  />
                </div>

                {/* Job Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title
                  </label>
                  <input
                    type="text"
                    value={editForm.jobTitle}
                    onChange={e => setEditForm({ ...editForm, jobTitle: e.target.value })}
                    className="input w-full"
                  />
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employment Start Date
                  </label>
                  <input
                    type="date"
                    value={editForm.startDate}
                    onChange={e => setEditForm({ ...editForm, startDate: e.target.value })}
                    className="input w-full"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Used for pro-rata leave calculations
                  </p>
                </div>

                {/* Department */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <select
                    value={editForm.department}
                    onChange={e => setEditForm({ ...editForm, department: e.target.value })}
                    className="input w-full"
                  >
                    <option value="">Select Department</option>
                    {DEPARTMENTS.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={editForm.role}
                    onChange={e => setEditForm({ ...editForm, role: e.target.value })}
                    className="input w-full"
                  >
                    {ROLES.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Reports To Section */}
                <div className="border-t border-gray-200 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Leave Recommender / Reports To
                  </label>

                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editForm.reportsDirectlyToDirector}
                        onChange={e => setEditForm({
                          ...editForm,
                          reportsDirectlyToDirector: e.target.checked,
                          supervisorId: e.target.checked ? '' : editForm.supervisorId
                        })}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">
                        Reports directly to Director (skip supervisor recommendation)
                      </span>
                    </label>

                    {!editForm.reportsDirectlyToDirector && (
                      <select
                        value={editForm.supervisorId}
                        onChange={e => setEditForm({ ...editForm, supervisorId: e.target.value })}
                        className="input w-full"
                      >
                        <option value="">Select Supervisor</option>
                        {getPotentialSupervisors().map(sup => (
                          <option key={sup.userId} value={sup.userId}>
                            {sup.firstName} {sup.lastName} ({sup.role})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {/* Status */}
                <div className="border-t border-gray-200 pt-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editForm.isActive}
                      onChange={e => setEditForm({ ...editForm, isActive: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Active Employee</span>
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                <button
                  onClick={handleCloseModal}
                  className="btn-secondary"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={isSaving}
                  className="btn-primary flex items-center gap-2"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={handleCloseAddModal}
            />

            {/* Modal */}
            <div className="relative w-full max-w-lg rounded-lg bg-white shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Add New Employee
                </h3>
                <button
                  onClick={handleCloseAddModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {createError && (
                  <div className="rounded-md bg-red-50 p-3">
                    <p className="text-sm text-red-700">{createError}</p>
                  </div>
                )}
                {createSuccess && (
                  <div className="rounded-md bg-green-50 p-3">
                    <p className="text-sm text-green-700">{createSuccess}</p>
                  </div>
                )}

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={addForm.email}
                    onChange={e => setAddForm({ ...addForm, email: e.target.value })}
                    placeholder="name@hcfs.org.za"
                    className="input w-full"
                  />
                </div>

                {/* Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={addForm.firstName}
                      onChange={e => setAddForm({ ...addForm, firstName: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={addForm.lastName}
                      onChange={e => setAddForm({ ...addForm, lastName: e.target.value })}
                      className="input w-full"
                    />
                  </div>
                </div>

                {/* Job Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title
                  </label>
                  <input
                    type="text"
                    value={addForm.jobTitle}
                    onChange={e => setAddForm({ ...addForm, jobTitle: e.target.value })}
                    placeholder="e.g. Social Worker, Teacher"
                    className="input w-full"
                  />
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employment Start Date
                  </label>
                  <input
                    type="date"
                    value={addForm.startDate}
                    onChange={e => setAddForm({ ...addForm, startDate: e.target.value })}
                    className="input w-full"
                  />
                </div>

                {/* Department */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <select
                    value={addForm.department}
                    onChange={e => setAddForm({ ...addForm, department: e.target.value })}
                    className="input w-full"
                  >
                    <option value="">Select Department</option>
                    {DEPARTMENTS.map(dept => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={addForm.role}
                    onChange={e => setAddForm({ ...addForm, role: e.target.value })}
                    className="input w-full"
                  >
                    {ROLES.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Reports To Section */}
                <div className="border-t border-gray-200 pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Leave Recommender / Reports To
                  </label>

                  <div className="space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={addForm.reportsDirectlyToDirector}
                        onChange={e => setAddForm({
                          ...addForm,
                          reportsDirectlyToDirector: e.target.checked,
                          supervisorId: e.target.checked ? '' : addForm.supervisorId
                        })}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">
                        Reports directly to Director (skip supervisor recommendation)
                      </span>
                    </label>

                    {!addForm.reportsDirectlyToDirector && (
                      <select
                        value={addForm.supervisorId}
                        onChange={e => setAddForm({ ...addForm, supervisorId: e.target.value })}
                        className="input w-full"
                      >
                        <option value="">Select Supervisor</option>
                        {getPotentialSupervisorsForAdd().map(sup => (
                          <option key={sup.userId} value={sup.userId}>
                            {sup.firstName} {sup.lastName} ({sup.role})
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                <button
                  onClick={handleCloseAddModal}
                  className="btn-secondary"
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateUser}
                  disabled={isCreating}
                  className="btn-primary flex items-center gap-2"
                >
                  {isCreating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Create Employee
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Balance Modal */}
      {isBalanceModalOpen && balanceUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={handleCloseBalanceModal}
            />

            {/* Modal */}
            <div className="relative w-full max-w-lg rounded-lg bg-white shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Manage Leave Balances
                  </h3>
                  <p className="text-sm text-gray-500">
                    {balanceUser.firstName} {balanceUser.lastName}
                  </p>
                </div>
                <button
                  onClick={handleCloseBalanceModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-4 space-y-4">
                {balanceError && (
                  <div className="rounded-md bg-red-50 p-3">
                    <p className="text-sm text-red-700">{balanceError}</p>
                  </div>
                )}
                {balanceSuccess && (
                  <div className="rounded-md bg-green-50 p-3">
                    <p className="text-sm text-green-700">{balanceSuccess}</p>
                  </div>
                )}

                {/* Year Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Year
                  </label>
                  <select
                    value={balanceYear}
                    onChange={e => handleYearChange(Number(e.target.value))}
                    className="input w-32"
                    disabled={isLoadingBalances}
                  >
                    {yearOptions.map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                {isLoadingBalances ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                    <span className="ml-2 text-gray-600">Loading balances...</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {LEAVE_TYPES_WITH_ALLOCATION.map(lt => (
                      <div key={lt.code} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900">{lt.name}</h4>
                            <p className="text-xs text-gray-500">{lt.description}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Total Allocation
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={balanceForm[lt.code]?.total ?? lt.defaultTotal}
                              onChange={e => setBalanceForm({
                                ...balanceForm,
                                [lt.code]: {
                                  ...balanceForm[lt.code],
                                  total: Number(e.target.value),
                                }
                              })}
                              className="input w-full text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Already Taken
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={balanceForm[lt.code]?.taken ?? 0}
                              onChange={e => setBalanceForm({
                                ...balanceForm,
                                [lt.code]: {
                                  ...balanceForm[lt.code],
                                  taken: Number(e.target.value),
                                }
                              })}
                              className="input w-full text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Carry Over
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={balanceForm[lt.code]?.carryOver ?? 0}
                              onChange={e => setBalanceForm({
                                ...balanceForm,
                                [lt.code]: {
                                  ...balanceForm[lt.code],
                                  carryOver: Number(e.target.value),
                                }
                              })}
                              className="input w-full text-sm"
                            />
                          </div>
                        </div>
                        <div className="mt-2 text-sm text-gray-600">
                          Available: <span className="font-medium text-green-600">
                            {(balanceForm[lt.code]?.total ?? lt.defaultTotal) -
                             (balanceForm[lt.code]?.taken ?? 0) +
                             (balanceForm[lt.code]?.carryOver ?? 0)} days
                          </span>
                        </div>
                      </div>
                    ))}

                    {/* Reason for change */}
                    <div className="border-t border-gray-200 pt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Reason for Change <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={balanceReason}
                        onChange={e => setBalanceReason(e.target.value)}
                        placeholder="e.g., Annual carry-over, correction for system migration, etc."
                        rows={2}
                        className="input w-full text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        This reason will be recorded in the audit log.
                      </p>
                    </div>

                    {/* History Toggle */}
                    <div className="border-t border-gray-200 pt-4">
                      <button
                        onClick={handleToggleHistory}
                        className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                      >
                        <History className="h-4 w-4" />
                        View Change History
                        {showHistory ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>

                      {showHistory && (
                        <div className="mt-3 max-h-64 overflow-y-auto">
                          {isLoadingHistory ? (
                            <div className="flex items-center justify-center py-4">
                              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                            </div>
                          ) : balanceHistory.length === 0 ? (
                            <p className="text-sm text-gray-500 py-2">No changes recorded yet.</p>
                          ) : (
                            <div className="space-y-3">
                              {balanceHistory.map((item) => (
                                <div key={item.auditId} className={`border rounded-lg p-3 ${
                                  item.auditType === 'LEAVE_APPROVED'
                                    ? 'border-green-200 bg-green-50'
                                    : item.auditType === 'LEAVE_CANCELLED'
                                    ? 'border-orange-200 bg-orange-50'
                                    : item.auditType === 'MANUAL_LEAVE_ENTRY'
                                    ? 'border-purple-200 bg-purple-50'
                                    : 'border-gray-100 bg-gray-50'
                                }`}>
                                  <div className="flex items-start justify-between">
                                    <div>
                                      <span className="font-medium text-gray-900">
                                        {getLeaveTypeName(item.leaveType)}
                                      </span>
                                      <span className="text-gray-500 text-xs ml-2">
                                        ({item.year})
                                      </span>
                                      {item.auditType === 'LEAVE_APPROVED' && (
                                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                          Leave Approved
                                        </span>
                                      )}
                                      {item.auditType === 'LEAVE_CANCELLED' && (
                                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                          Leave Cancelled
                                        </span>
                                      )}
                                      {item.auditType === 'MANUAL_LEAVE_ENTRY' && (
                                        <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                          Manual Entry
                                        </span>
                                      )}
                                    </div>
                                    <span className="text-xs text-gray-500">
                                      {formatHistoryTimestamp(item.timestamp)}
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    {item.auditType === 'LEAVE_APPROVED' || item.auditType === 'MANUAL_LEAVE_ENTRY' ? (
                                      <span>
                                        Deducted: {item.daysDeducted} days
                                        <span className="text-gray-400 ml-1">
                                          (Taken: {item.previousBalance?.taken || 0} → {item.newBalance.taken})
                                        </span>
                                      </span>
                                    ) : item.auditType === 'LEAVE_CANCELLED' ? (
                                      <span>
                                        Restored: {item.daysRestored} days
                                        <span className="text-gray-400 ml-1">
                                          (Taken: {item.previousBalance?.taken || 0} → {item.newBalance.taken})
                                        </span>
                                      </span>
                                    ) : item.previousBalance ? (
                                      <span>
                                        Total: {item.previousBalance.total} → {item.newBalance.total},
                                        Taken: {item.previousBalance.taken} → {item.newBalance.taken}
                                      </span>
                                    ) : (
                                      <span>Set to: Total {item.newBalance.total}, Taken {item.newBalance.taken}</span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    <span className="italic">"{item.reason}"</span>
                                    <span className="ml-2">— {item.performedByName}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                <button
                  onClick={handleCloseBalanceModal}
                  className="btn-secondary"
                  disabled={isSavingBalances}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveBalances}
                  disabled={isSavingBalances || isLoadingBalances}
                  className="btn-primary flex items-center gap-2"
                >
                  {isSavingBalances ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Save Balances
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Allocation Modal */}
      {isBulkAllocationOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={handleCloseBulkAllocation}
            />

            {/* Modal */}
            <div className="relative w-full max-w-2xl rounded-lg bg-white shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Allocate Annual Leave Balances
                  </h3>
                  <p className="text-sm text-gray-500">
                    Initialize leave balances for all active employees
                  </p>
                </div>
                <button
                  onClick={handleCloseBulkAllocation}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={bulkAllocationProgress.status === 'processing'}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                {bulkAllocationProgress.status === 'idle' && (
                  <>
                    {/* Year Selection and Allocation Amounts */}
                    <div className="mb-6 grid grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Year
                        </label>
                        <select
                          value={bulkAllocationYear}
                          onChange={e => setBulkAllocationYear(Number(e.target.value))}
                          className="input w-full"
                        >
                          {[new Date().getFullYear(), new Date().getFullYear() + 1].map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Annual Leave
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={bulkAllocations.annual}
                          onChange={e => setBulkAllocations({ ...bulkAllocations, annual: Number(e.target.value) })}
                          className="input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Sick Leave
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={bulkAllocations.sick}
                          onChange={e => setBulkAllocations({ ...bulkAllocations, sick: Number(e.target.value) })}
                          className="input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Family Resp.
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={bulkAllocations.family}
                          onChange={e => setBulkAllocations({ ...bulkAllocations, family: Number(e.target.value) })}
                          className="input w-full"
                        />
                      </div>
                    </div>

                    {/* Preview Users */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">
                        Users to Allocate ({getActiveUsers().length} active employees)
                      </h4>
                      <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-64 overflow-y-auto">
                        {getActiveUsers().map(user => (
                          <div key={user.userId} className="px-4 py-2 flex items-center justify-between hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handleRemoveFromBulkAllocation(user.userId)}
                                className="text-gray-400 hover:text-red-500"
                                title="Remove from allocation"
                              >
                                <X className="h-4 w-4" />
                              </button>
                              <div>
                                <span className="font-medium text-gray-900">
                                  {user.firstName} {user.lastName}
                                </span>
                                <span className="text-sm text-gray-500 ml-2">
                                  {user.departmentName || user.department}
                                </span>
                              </div>
                            </div>
                            <div className="text-xs text-gray-500">
                              A:{bulkAllocations.annual} S:{bulkAllocations.sick} F:{bulkAllocations.family}
                            </div>
                          </div>
                        ))}
                        {getActiveUsers().length === 0 && (
                          <div className="px-4 py-8 text-center text-gray-500">
                            No users to allocate. All users have been removed.
                          </div>
                        )}
                      </div>
                      {bulkExcludedUsers.size > 0 && (
                        <p className="mt-2 text-xs text-gray-500">
                          {bulkExcludedUsers.size} user(s) removed from allocation.{' '}
                          <button
                            onClick={() => setBulkExcludedUsers(new Set())}
                            className="text-primary-600 hover:underline"
                          >
                            Restore all
                          </button>
                        </p>
                      )}
                    </div>

                    {/* Warning Note */}
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-700">
                        <strong>Note:</strong> Existing balances for {bulkAllocationYear} will be reset to these values.
                        Use this at the start of a new leave year.
                      </p>
                    </div>
                  </>
                )}

                {bulkAllocationProgress.status === 'processing' && (
                  <div className="py-8">
                    <div className="flex items-center justify-center mb-4">
                      <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                    </div>
                    <p className="text-center text-gray-700 mb-2">
                      Processing allocations...
                    </p>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
                      <div
                        className="bg-primary-600 h-2.5 rounded-full transition-all"
                        style={{
                          width: `${(bulkAllocationProgress.current / bulkAllocationProgress.total) * 100}%`
                        }}
                      />
                    </div>
                    <p className="text-center text-sm text-gray-500">
                      {bulkAllocationProgress.current} of {bulkAllocationProgress.total} users
                    </p>
                  </div>
                )}

                {bulkAllocationProgress.status === 'complete' && (
                  <div>
                    <div className="flex items-center justify-center mb-4">
                      <CheckCircle className="h-12 w-12 text-green-500" />
                    </div>
                    <h4 className="text-center text-lg font-medium text-gray-900 mb-4">
                      Allocation Complete
                    </h4>
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-64 overflow-y-auto">
                      {bulkAllocationProgress.results.map((result, idx) => (
                        <div key={idx} className="px-4 py-2 flex items-center justify-between">
                          <span className="text-sm text-gray-900">{result.name}</span>
                          {result.success ? (
                            <span className="flex items-center gap-1 text-green-600 text-sm">
                              <Check className="h-4 w-4" />
                              Success
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600 text-sm">
                              <AlertCircle className="h-4 w-4" />
                              {result.error || 'Failed'}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 text-center text-sm text-gray-600">
                      {bulkAllocationProgress.results.filter(r => r.success).length} of{' '}
                      {bulkAllocationProgress.results.length} users allocated successfully
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                <button
                  onClick={handleCloseBulkAllocation}
                  className="btn-secondary"
                  disabled={bulkAllocationProgress.status === 'processing'}
                >
                  {bulkAllocationProgress.status === 'complete' ? 'Close' : 'Cancel'}
                </button>
                {bulkAllocationProgress.status === 'idle' && (
                  <button
                    onClick={handleStartBulkAllocation}
                    disabled={getActiveUsers().length === 0}
                    className="btn-primary flex items-center gap-2"
                  >
                    <CalendarPlus className="h-4 w-4" />
                    Allocate to {getActiveUsers().length} Users
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Leave Entry Modal */}
      {isManualLeaveOpen && manualLeaveUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={handleCloseManualLeaveModal}
            />

            {/* Modal */}
            <div className="relative w-full max-w-lg rounded-lg bg-white shadow-xl">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Add Leave Record
                  </h3>
                  <p className="text-sm text-gray-500">
                    {manualLeaveUser.firstName} {manualLeaveUser.lastName}
                  </p>
                </div>
                <button
                  onClick={handleCloseManualLeaveModal}
                  className="rounded-full p-1 hover:bg-gray-100"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-4 space-y-4">
                {manualLeaveError && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {manualLeaveError}
                  </div>
                )}

                {manualLeaveSuccess && (
                  <div className="rounded-md bg-green-50 p-3 text-sm text-green-700 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    {manualLeaveSuccess}
                  </div>
                )}

                {/* Leave Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Leave Type *
                  </label>
                  <select
                    value={manualLeaveForm.leaveType}
                    onChange={e => handleManualLeaveFormChange('leaveType', e.target.value)}
                    className="input w-full"
                    disabled={isCreatingLeave}
                  >
                    <option value="ANNUAL">Annual Leave</option>
                    <option value="SICK">Sick Leave</option>
                    <option value="FAMILY">Family Responsibility</option>
                    <option value="MATERNITY">Maternity Leave</option>
                    <option value="PATERNITY">Paternity Leave</option>
                    <option value="STUDY">Study Leave</option>
                    <option value="UNPAID">Unpaid Leave</option>
                    <option value="SPECIAL">Special Leave</option>
                  </select>
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      value={manualLeaveForm.startDate}
                      onChange={e => {
                        handleManualLeaveFormChange('startDate', e.target.value);
                        // Auto-calculate days
                        if (manualLeaveForm.endDate) {
                          const days = calculateDaysBetween(e.target.value, manualLeaveForm.endDate);
                          handleManualLeaveFormChange('totalDays', days);
                        }
                      }}
                      className="input w-full"
                      disabled={isCreatingLeave}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date *
                    </label>
                    <input
                      type="date"
                      value={manualLeaveForm.endDate}
                      onChange={e => {
                        handleManualLeaveFormChange('endDate', e.target.value);
                        // Auto-calculate days
                        if (manualLeaveForm.startDate) {
                          const days = calculateDaysBetween(manualLeaveForm.startDate, e.target.value);
                          handleManualLeaveFormChange('totalDays', days);
                        }
                      }}
                      className="input w-full"
                      disabled={isCreatingLeave}
                    />
                  </div>
                </div>

                {/* Total Days */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Days
                  </label>
                  <input
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={manualLeaveForm.totalDays}
                    onChange={e => handleManualLeaveFormChange('totalDays', Number(e.target.value))}
                    className="input w-full"
                    disabled={isCreatingLeave}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Auto-calculated from dates. Adjust manually if needed (e.g., for half days or excluding weekends).
                  </p>
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason *
                  </label>
                  <input
                    type="text"
                    value={manualLeaveForm.reason}
                    onChange={e => handleManualLeaveFormChange('reason', e.target.value)}
                    className="input w-full"
                    placeholder="e.g., Historical leave entry, System migration"
                    disabled={isCreatingLeave}
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Notes
                  </label>
                  <textarea
                    value={manualLeaveForm.notes}
                    onChange={e => handleManualLeaveFormChange('notes', e.target.value)}
                    className="input w-full"
                    rows={2}
                    placeholder="Optional notes..."
                    disabled={isCreatingLeave}
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> This will create an approved leave record and immediately deduct {manualLeaveForm.totalDays} day(s) from {manualLeaveUser.firstName}'s {getLeaveTypeName(manualLeaveForm.leaveType)} balance.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
                <button
                  onClick={handleCloseManualLeaveModal}
                  className="btn-secondary"
                  disabled={isCreatingLeave}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateManualLeave}
                  disabled={isCreatingLeave || !manualLeaveForm.startDate || !manualLeaveForm.endDate || !manualLeaveForm.reason.trim()}
                  className="btn-primary flex items-center gap-2"
                >
                  {isCreatingLeave ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                  Create Leave Record
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
